import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { User, UserMetaData } from '../../../../../core/models/user.model';
import { UserService } from '../../../../../core/services/user.service';

interface DialogUserMetaViewModel {
  readonly displayName: string;
  readonly email: string;
  readonly initials: string;
  readonly isConnected: boolean;
}

@Component({
  selector: 'app-si-dialog-user-meta',
  templateUrl: './si-dialog-user-meta.component.html',
  styleUrls: ['./si-dialog-user-meta.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class SiDialogUserMetaComponent {
  readonly emailFieldInput = input.required<keyof UserMetaData>();
  readonly nameFieldInput = input.required<keyof UserMetaData>();
  readonly notConnectedTextInput = input('Not Connected Yet');

  private readonly userService: UserService = inject(UserService);
  private readonly userDataSignal = toSignal(this.userService.getUserData(), {
    initialValue: undefined as User | undefined,
  });

  readonly userMetaViewModel = computed<DialogUserMetaViewModel | null>(() => {
    const user: User | undefined = this.userDataSignal();
    if (!user) {
      return null;
    }

    const userMeta: UserMetaData = this.getUserMeta(user);
    const displayName: string = this.getTrimmedUserMetaValue(userMeta, this.nameFieldInput());
    const email: string = this.getTrimmedUserMetaValue(userMeta, this.emailFieldInput());
    const isConnected: boolean = Boolean(displayName || email);

    return {
      displayName: displayName || email || this.notConnectedTextInput(),
      email: displayName && email ? email : '',
      initials: this.getInitials(displayName, email),
      isConnected,
    };
  });

  private getInitials(displayName: string, email: string): string {
    if (displayName) {
      const nameParts: string[] = displayName.split(/\s+/).filter(Boolean);
      const initials: string = nameParts
        .slice(0, 2)
        .map((part: string) => part.charAt(0))
        .join('');

      if (initials) {
        return initials.toUpperCase();
      }
    }

    return email ? email.charAt(0).toUpperCase() : '';
  }

  private getTrimmedUserMetaValue(userMeta: UserMetaData, field: keyof UserMetaData): string {
    const value: UserMetaData[keyof UserMetaData] = userMeta[field];
    return typeof value === 'string' ? value.trim() : '';
  }

  private getUserMeta(user: User): UserMetaData {
    return (user?.user_meta ?? {}) as UserMetaData;
  }
}
